import { useEffect, useState } from 'react';
import { Check, Copy, Eye, Link2, Loader2, Mail, MessageCircle, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { ConsultaContrato, Contrato, PreparacionConsulta } from '@/lib/contratos';
import { classNames, formatDate, formatDateTime } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * Enviar el contrato para que lo lean, sin Click & Sign.
 *
 * Existe porque «¿me enseñas el contrato?» se resolvía mandándolo a firmar, y
 * Click & Sign cobra cada envío aunque nadie firme. Esto manda un enlace de
 * solo lectura: la página enseña el texto y deja descargar un PDF marcado
 * como copia de consulta, sin campos ni firma.
 */

type Estado = 'vigente' | 'caducada' | 'revocada';

const ESTILO_ESTADO: Record<Estado, { label: string; className: string }> = {
  vigente: { label: 'Activo', className: 'bg-emerald-100 text-emerald-700' },
  caducada: { label: 'Caducado', className: 'bg-gray-100 text-gray-500' },
  revocada: { label: 'Revocado', className: 'bg-red-100 text-red-600' },
};

const VALIDEZ = [7, 15, 30, 60, 90];

function estadoDe(c: ConsultaContrato, contrato: Contrato): Estado {
  if (c.revocadaAt || contrato.estado === 'anulado') return 'revocada';
  return new Date(c.expiraAt).getTime() <= Date.now() ? 'caducada' : 'vigente';
}

async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    window.prompt('Copia el enlace:', texto);
    return false;
  }
}

function BotonCopiar({ url, className }: { url: string; className?: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => { if (await copiar(url)) { setCopiado(true); setTimeout(() => setCopiado(false), 2000); } }}
      className={classNames('flex items-center gap-1', className)}
    >
      {copiado ? <Check size={12} /> : <Copy size={12} />} {copiado ? 'Copiado' : 'Copiar enlace'}
    </button>
  );
}

function enlaceWhatsApp(telefono: string, url: string, nombre: string): string | null {
  const digitos = telefono.replace(/\D/g, '');
  if (digitos.length < 9) return null;
  const numero = digitos.length === 9 ? `34${digitos}` : digitos;
  const primer = nombre.trim().split(/\s+/)[0];
  const texto = `Hola${primer ? ` ${primer}` : ''}, te paso el contrato para que lo leas con calma. Es solo para consultarlo, no tienes que firmar nada: ${url}`;
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

function NuevaConsultaModal({ contrato, abierto, onClose, onCreada }: {
  contrato: Contrato;
  abierto: boolean;
  onClose: () => void;
  onCreada: (c: Contrato) => void;
}) {
  const [prep, setPrep] = useState<PreparacionConsulta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [dias, setDias] = useState(30);
  const [porCorreo, setPorCorreo] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState<{ url: string; correo: { estado: string; detalle?: string }; porCorreo: boolean } | null>(null);

  useEffect(() => {
    if (!abierto) return;
    let vigente = true;
    setPrep(null); setError(null); setHecho(null); setDias(30); setPorCorreo(true); setMensaje('');
    api.prepararConsultaContrato(contrato.id).then(p => {
      if (!vigente) return;
      setPrep(p);
      setEmail(p.email);
      setNombre(p.nombre);
    }).catch(e => { if (vigente) setError(e.message); });
    return () => { vigente = false; };
  }, [abierto, contrato.id]);

  const valido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const crear = async () => {
    if (!valido) return;
    setEnviando(true);
    try {
      const r = await api.crearConsultaContrato(contrato.id, {
        email: email.trim(),
        nombre: nombre.trim() || null,
        // Solo viaja si sale correo: sin correo no hay dónde ponerlo.
        mensaje: porCorreo ? mensaje.trim() || null : null,
        dias,
        enviarCorreo: porCorreo,
      });
      onCreada(r.contrato);
      setHecho({ url: r.url, correo: r.correo, porCorreo });
    } catch (e: any) {
      alert(e.message || 'No se pudo crear el enlace');
    } finally {
      setEnviando(false);
    }
  };

  const whatsapp = hecho && prep?.telefono ? enlaceWhatsApp(prep.telefono, hecho.url, nombre) : null;

  return (
    <Modal isOpen={abierto} onClose={onClose} title={`Enviar para consultar · ${contrato.codigo}`} size="lg">
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !prep ? (
        <LoadingSpinner />
      ) : hecho ? (
        <div className="space-y-4">
          {hecho.porCorreo && hecho.correo.estado === 'enviado' ? (
            <p className="text-sm flex items-center gap-2 text-emerald-700"><Check size={16} /> Correo enviado a {email.trim()}.</p>
          ) : hecho.porCorreo ? (
            <p className="text-sm text-amber-700">El enlace está creado, pero el correo no salió{hecho.correo.detalle ? ` (${hecho.correo.detalle})` : ''}. Cópialo y mándaselo tú.</p>
          ) : (
            <p className="text-sm text-sereno-dark/70">Enlace creado. Mándaselo por donde prefieras.</p>
          )}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
            <input readOnly value={hecho.url} className="flex-1 min-w-0 bg-transparent text-xs font-mono outline-none" onFocus={e => e.currentTarget.select()} />
            <BotonCopiar url={hecho.url} className="btn-secondary text-xs whitespace-nowrap" />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-gray-100">
            {whatsapp && (
              <a href={whatsapp} target="_blank" rel="noreferrer" className="btn-secondary text-sm flex items-center gap-1.5"><MessageCircle size={14} /> Mandar por WhatsApp</a>
            )}
            <button onClick={onClose} className="btn-primary text-sm">Hecho</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-sereno-dark/70">
            Le llega un enlace para leer el contrato en el móvil y descargarlo en PDF. No pasa por Click &amp; Sign y desde ahí no se puede firmar.
            El DNI y el IBAN salen ocultos.
          </p>
          {contrato.estado === 'borrador' && (
            <p className="text-xs rounded-lg bg-amber-50 text-amber-800 p-2">Es un borrador: se envía el texto tal y como está guardado ahora. Si luego lo cambias, manda un enlace nuevo.</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Correo</label>
              <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="propietario@correo.com" />
            </div>
            <div>
              <label className="label">Nombre para el saludo</label>
              <input className="input" value={nombre} onChange={e => setNombre(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 items-end">
            <div>
              <label className="label">El enlace dura</label>
              <select className="input" value={dias} onChange={e => setDias(Number(e.target.value))}>
                {VALIDEZ.map(d => <option key={d} value={d}>{d} días</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
              <input type="checkbox" checked={porCorreo} onChange={e => setPorCorreo(e.target.checked)} />
              Mandárselo por correo ahora
            </label>
          </div>
          {porCorreo && (
            <div>
              <label className="label" htmlFor="consulta-mensaje">Mensaje para el correo (opcional)</label>
              <textarea
                id="consulta-mensaje"
                className="input resize-none"
                rows={3}
                maxLength={2000}
                value={mensaje}
                onChange={e => setMensaje(e.target.value)}
                placeholder="Por ejemplo: Como hablamos por teléfono, aquí tienes el contrato con las condiciones que acordamos."
              />
              <p className="text-xs text-sereno-dark/50 mt-1">Va justo después del saludo, antes del texto de siempre.</p>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
            <button onClick={crear} disabled={!valido || enviando} className="btn-primary text-sm flex items-center gap-1.5">
              {enviando ? <Loader2 size={14} className="animate-spin" /> : porCorreo ? <Mail size={14} /> : <Link2 size={14} />}
              {porCorreo ? 'Enviar enlace' : 'Crear enlace'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function ConsultaCard({ contrato, onCambio, cambiosSinGuardar = false }: {
  contrato: Contrato;
  onCambio: (c: Contrato) => void;
  /** En un borrador, lo que se envía es lo guardado: con cambios pendientes se pide guardar antes. */
  cambiosSinGuardar?: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const [revocando, setRevocando] = useState<string | null>(null);
  const consultas = [...(contrato.consultas || [])].sort((a, b) => b.creadaAt.localeCompare(a.creadaAt));

  const revocar = async (c: ConsultaContrato) => {
    if (!confirm(`¿Revocar el enlace de ${c.email}? Dejará de abrirse.`)) return;
    setRevocando(c.id);
    try {
      onCambio(await api.revocarConsultaContrato(contrato.id, c.id));
    } catch (e: any) {
      alert(e.message || 'No se pudo revocar');
    } finally {
      setRevocando(null);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">Enviar para consultar</p>
        <span className="badge bg-sereno-blue/40 text-sereno-dark text-[10px]">Sin Click &amp; Sign</span>
      </div>
      <p className="text-xs text-sereno-dark/50">Para quien pide «ver el contrato»: un enlace para leerlo y descargarlo, sin firma y sin gastar envíos de Click &amp; Sign.</p>
      {contrato.estado !== 'anulado' && <button
        onClick={() => setAbierto(true)}
        disabled={cambiosSinGuardar}
        title={cambiosSinGuardar ? 'Guarda los cambios antes de enviarlo' : undefined}
        className="btn-secondary text-sm w-full flex items-center justify-center gap-1.5"
      >
        <Eye size={14} /> Enviar para consultar
      </button>}
      {cambiosSinGuardar && <p className="text-xs text-amber-700">Guarda los cambios antes de enviarlo: se manda lo guardado.</p>}

      {consultas.length > 0 && (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {consultas.map(c => {
            const estado = estadoDe(c, contrato);
            const url = api.urlConsultaContrato(c.token);
            return (
              <li key={c.id} className="text-xs space-y-0.5 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate" title={c.email}>{c.email}</span>
                  <span className={classNames('badge shrink-0', ESTILO_ESTADO[estado].className)}>{ESTILO_ESTADO[estado].label}</span>
                </div>
                <p className="text-sereno-dark/50">
                  {formatDateTime(c.creadaAt)} · {estado === 'caducada' ? 'caducó' : 'caduca'} el {formatDate(c.expiraAt)}
                  {c.correo.estado === 'enviado' ? ' · por correo' : c.correo.estado === 'no_enviado' ? ' · enlace copiado' : ''}
                </p>
                {(c.correo.estado === 'error' || c.correo.estado === 'no_configurado') && (
                  <p className="text-amber-700">El correo no salió: copia el enlace y mándaselo.</p>
                )}
                <p className={c.vistas > 0 ? 'text-emerald-700' : 'text-sereno-dark/50'}>
                  {c.vistas > 0
                    ? `Abierto ${c.vistas === 1 ? '1 vez' : `${c.vistas} veces`}${c.ultimaVistaAt ? ` · última ${formatDateTime(c.ultimaVistaAt)}` : ''}${c.descargas ? ` · ${c.descargas} descarga${c.descargas === 1 ? '' : 's'}` : ''}`
                    : 'Sin abrir todavía'}
                </p>
                {estado === 'vigente' && (
                  <div className="flex items-center gap-3 pt-0.5">
                    <BotonCopiar url={url} className="text-sereno-dark/70 hover:text-sereno-dark" />
                    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sereno-dark/70 hover:text-sereno-dark"><Eye size={12} /> Ver</a>
                    <button onClick={() => revocar(c)} disabled={revocando === c.id} className="flex items-center gap-1 text-red-500 hover:text-red-700 ml-auto">
                      {revocando === c.id ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Revocar
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <NuevaConsultaModal contrato={contrato} abierto={abierto} onClose={() => setAbierto(false)} onCreada={onCambio} />
    </div>
  );
}
